import * as THREE from 'three';
import { RoomEnvironment } from 'three/examples/jsm/environments/RoomEnvironment.js';
import { JAR_TYPES } from './jarMeta';
import { loadJarModel, buildJar } from './jarModel';
import { logError } from '../../lib/log';

const SIZE = 420;
const STORE_CAM = { pos: [0.66, 1.04, 2.0], look: [0, 0.37, 0] };
const SHARE_CAM = { pos: [0.6, 2.0, 2.3], look: [0, 0.3, 0] };
const setCam = (camera, { pos, look }) => { camera.position.set(...pos); camera.lookAt(...look); };

const cache = new Map();
const queue = [];
let ctx = null;
let parts = null;
let scheduled = false;
let releaseTimer = null;

const release = () => {
  if (!ctx) return;
  const { renderer, scene, pmrem } = ctx;
  scene.environment?.dispose();
  pmrem.dispose();
  renderer.dispose();
  renderer.forceContextLoss();
  ctx = null;
};

const getCtx = () => {
  clearTimeout(releaseTimer);
  if (ctx) return ctx;
  const renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true, preserveDrawingBuffer: true });
  renderer.setSize(SIZE, SIZE, false);
  renderer.setClearColor(0x000000, 0);
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  const scene = new THREE.Scene();
  const pmrem = new THREE.PMREMGenerator(renderer);
  scene.environment = pmrem.fromScene(new RoomEnvironment(), 0.04).texture;
  scene.environmentIntensity = 0.55;
  const camera = new THREE.PerspectiveCamera(28, 1, 0.1, 20);
  setCam(camera, STORE_CAM);
  scene.add(new THREE.AmbientLight(0xffffff, 0.3));
  const spot = new THREE.SpotLight(0xffffff, 28, 0, 0.6, 1);
  spot.position.set(3, 6, 4);
  scene.add(spot);
  const pink = new THREE.PointLight(0xff69b4, 6);
  pink.position.set(-3, 2, 2);
  scene.add(pink);
  const blue = new THREE.PointLight(0x4da8ff, 6);
  blue.position.set(3, 1, -2);
  scene.add(blue);
  ctx = { renderer, scene, camera, pmrem };
  return ctx;
};

export const snapshotKey = (color, type) => `${color}|${type}`;

export const renderJarSnapshot = (color, type, label) => {
  const key = snapshotKey(color, type);
  if (cache.has(key)) return cache.get(key);
  const { renderer, scene, camera } = getCtx();
  const meta = JAR_TYPES[type] || JAR_TYPES.crema_giorno;
  const jar = buildJar(parts, { color, icon: meta.icon, label: label || meta.label });
  jar.group.rotation.y = -0.3;
  scene.add(jar.group);
  renderer.render(scene, camera);
  const url = renderer.domElement.toDataURL('image/png');
  scene.remove(jar.group);
  jar.dispose();
  cache.set(key, url);
  return url;
};

const pump = () => {
  if (!parts) {
    loadJarModel().then((p) => { parts = p; pump(); }, (err) => {
      logError('Jar model failed to load:', err);
      scheduled = false;
      queue.splice(0).forEach((job) => job.cb(null));
    });
    return;
  }
  scheduled = false;
  const job = queue.shift();
  if (!job) return;
  try {
    job.cb(renderJarSnapshot(job.color, job.type, job.label));
  } catch (err) {
    logError('Jar snapshot failed:', err);
    job.cb(null);
  }
  if (queue.length && !scheduled) {
    scheduled = true;
    requestAnimationFrame(pump);
  } else if (!queue.length) {
    clearTimeout(releaseTimer);
    releaseTimer = setTimeout(release, 1500);
  }
};

export const requestJarSnapshot = (color, type, cb, label) => {
  const key = snapshotKey(color, type);
  if (cache.has(key)) { cb(cache.get(key)); return; }
  queue.push({ color, type, label, cb });
  if (!scheduled) {
    scheduled = true;
    requestAnimationFrame(pump);
  }
};

export const getCachedSnapshot = (color, type) => cache.get(snapshotKey(color, type)) || null;

const hdCache = new Map();

const trimAlpha = (source) => {
  const w = source.width;
  const h = source.height;
  const c = document.createElement('canvas');
  c.width = w;
  c.height = h;
  const g = c.getContext('2d');
  g.drawImage(source, 0, 0);
  const { data } = g.getImageData(0, 0, w, h);
  let x0 = w; let y0 = h; let x1 = -1; let y1 = -1;
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      if (data[(y * w + x) * 4 + 3] > 8) {
        if (x < x0) x0 = x;
        if (x > x1) x1 = x;
        if (y < y0) y0 = y;
        if (y > y1) y1 = y;
      }
    }
  }
  if (x1 < 0) return c;
  const pad = 6;
  x0 = Math.max(0, x0 - pad); y0 = Math.max(0, y0 - pad);
  x1 = Math.min(w - 1, x1 + pad); y1 = Math.min(h - 1, y1 + pad);
  const out = document.createElement('canvas');
  out.width = x1 - x0 + 1;
  out.height = y1 - y0 + 1;
  out.getContext('2d').drawImage(c, x0, y0, out.width, out.height, 0, 0, out.width, out.height);
  return out;
};

export const renderJarImages = async (jars, size = 720) => {
  const keys = jars.map(({ color, type, label }) => `${color}|${type}|${(label || []).join('/')}|${size}`);
  if (keys.every((k) => hdCache.has(k))) return keys.map((k) => hdCache.get(k));
  if (!parts) parts = await loadJarModel();
  const { renderer, scene, camera } = getCtx();
  renderer.setSize(size, size, false);
  setCam(camera, SHARE_CAM);
  const out = jars.map(({ color, type, label }, i) => {
    if (hdCache.has(keys[i])) return hdCache.get(keys[i]);
    const meta = JAR_TYPES[type] || JAR_TYPES.crema_giorno;
    const jar = buildJar(parts, { color, icon: meta.icon, label: label || meta.label });
    jar.group.rotation.y = -0.3;
    scene.add(jar.group);
    renderer.render(scene, camera);
    const img = trimAlpha(renderer.domElement);
    scene.remove(jar.group);
    jar.dispose();
    hdCache.set(keys[i], img);
    return img;
  });
  renderer.setSize(SIZE, SIZE, false);
  setCam(camera, STORE_CAM);
  if (!queue.length) {
    clearTimeout(releaseTimer);
    releaseTimer = setTimeout(release, 1500);
  }
  return out;
};
